import React from 'react';
import { Quote, ArrowRight, Award } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import TestimonialsWidget from '../components/testimonials/TestimonialsWidget';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import heroResults from '@/assets/hero-results.jpg';
import textureWash from '@/assets/leaders-texture-wash.jpg';
import jojoMinor from '@/assets/jojo-minor.png';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

// ============ Hero - asymmetric editorial ============
const ResultsHero = () => {
  const lead = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative pt-32 pb-24 sm:pt-44 sm:pb-32 overflow-hidden bg-foreground">
      <div className="absolute inset-0">
        <img
          src={heroResults}
          alt=""
          className="w-full h-full object-cover object-center opacity-30"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-foreground/75" />
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 15% 80%, hsl(var(--primary) / 0.22) 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-end">
          <div
            ref={lead.ref}
            className={`md:col-span-7 transition-all duration-1000 ${
              lead.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-primary" aria-hidden="true" />
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                The Results
              </p>
            </div>
            <h1
              className="text-background text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] mb-10"
              style={{ textWrap: 'balance' }}
            >
              The leaders our clients <span className="text-primary italic">become.</span>
            </h1>
            <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-normal">
              More views. New ways. Inspired outcomes.
            </p>
          </div>

          <div
            ref={tail.ref}
            className={`md:col-span-5 md:pl-8 md:border-l md:border-background/15 transition-all duration-1000 delay-200 ${
              tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-5">
              The Compounding
            </p>
            <p className="text-background/80 text-lg leading-relaxed font-normal">
              What changes in their meetings, decisions, relationships, and the cultures they build.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ Featured Testimonial - dark editorial spread ============
const FeaturedTestimonial = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src={textureWash} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.16) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-4xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="flex items-center gap-4 mb-10">
          <span className="h-px w-12 bg-primary" aria-hidden="true" />
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
            In Their Words
          </p>
        </div>
        <Quote className="h-12 w-12 text-primary mb-8" />
        <blockquote className="space-y-8">
          <p
            className="text-2xl sm:text-3xl lg:text-4xl text-background leading-snug font-display italic"
            style={{ textWrap: 'balance' }}
          >
            This is the single best enablement program I have ever been a part of. ParadoxEdge coaching helps me develop the skills I need to be a more present leader, parent, partner, daughter, sister, friend.
          </p>
          <footer className="flex items-center gap-4 pt-4">
            <img
              src={jojoMinor}
              alt="JoJo Minor"
              className="w-14 h-14 rounded-full object-cover"
            />
            <div>
              <cite className="not-italic font-semibold text-primary text-lg block">JoJo Minor</cite>
              <p className="text-background/60 text-sm">Strategic Account Team, Amazon Web Services</p>
            </div>
          </footer>
        </blockquote>
      </div>
    </section>
  );
};

// ============ Measurable Impact - editorial stat grid (light) ============
const MeasurableImpact = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const stats = useScrollReveal<HTMLDivElement>();

  const items = [
    { value: '↑ 26%', label: 'Thriving at Work', context: 'Greater engagement and fulfillment reported across cohorts.' },
    { value: '↑ 34%', label: 'Team Strengths Awareness', context: 'Stronger collaboration and shared understanding.' },
    { value: '↑ 30%', label: 'Conflict Resolution', context: 'Capacity to navigate hard conversations productively.' },
    { value: '↓ 40%', label: 'Work-Related Stress', context: 'Sustained reduction in chronic stress over six months.' },
  ];

  return (
    <section className="relative bg-secondary/60 text-foreground py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.12) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, hsl(var(--primary) / 0.08) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Measurable Impact
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
            style={{ textWrap: 'balance' }}
          >
            What the work <span className="text-primary italic">produces.</span>
          </h2>
          <p className="text-foreground/90 text-xl leading-relaxed font-normal max-w-2xl">
            Outcomes reported by participants across multi-year engagements.
          </p>
        </div>

        <div
          ref={stats.ref}
          className={`grid sm:grid-cols-2 gap-px bg-foreground/10 border border-foreground/10 transition-all duration-1000 ${
            stats.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {items.map((item, i) => (
            <div
              key={i}
              className="group relative bg-background p-10 sm:p-12 hover:bg-secondary/40 transition-colors duration-500"
            >
              <div className="w-10 h-px bg-primary mb-6" aria-hidden="true" />
              <div className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary mb-4 leading-none">
                {item.value}
              </div>
              <p className="text-foreground font-display text-xl mb-3">{item.label}</p>
              <p className="text-foreground/85 text-sm leading-relaxed font-normal">
                {item.context}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ NPS - dark contemplative band ============
const NPS = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-5xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 text-center md:text-left">
            <div className="flex items-center gap-4 mb-8 justify-center md:justify-start">
              <span className="h-px w-12 bg-primary" aria-hidden="true" />
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                Net Promoter Score
              </p>
            </div>
            <div className="font-display text-[8rem] sm:text-[10rem] lg:text-[12rem] text-primary leading-none">
              91
            </div>
          </div>
          <div className="md:col-span-7 md:pl-8 md:border-l md:border-background/15">
            <Award className="h-10 w-10 text-primary mb-6" />
            <h2
              className="text-background text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-6"
              style={{ textWrap: 'balance' }}
            >
              Zero detractors. <span className="text-primary italic">Across every cohort.</span>
            </h2>
            <p className="text-background/75 text-lg leading-relaxed font-normal">
              The clearest signal the work lands. Participants recommend it to the people closest to them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ Voices - Elfsight container restyled ============
const Voices = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.10) 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Voices From the Practice
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            What our clients <span className="text-primary italic">say.</span>
          </h2>
        </div>

        <TestimonialsWidget />
      </div>
    </section>
  );
};

// ============ Final CTA ============
const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          Who do you need to <span className="text-primary italic">become?</span>
        </h2>
        <Button
          onClick={handleScheduleCall}
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
        >
          Begin a Conversation
          <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const testimonialsJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "ParadoxEdge | Client Results & Testimonials",
      "url": "https://www.paradoxedge.com/testimonials",
      "description": "Outcomes and voices from senior leaders who have engaged ParadoxEdge coaching. Net Promoter Score 91. Zero detractors.",
      "isPartOf": { "@type": "WebSite", "name": "ParadoxEdge", "url": "https://www.paradoxedge.com" }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ParadoxEdge",
      "url": "https://www.paradoxedge.com",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "bestRating": "5",
        "ratingCount": "20",
        "reviewCount": "20"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.paradoxedge.com/" },
        { "@type": "ListItem", "position": 2, "name": "Results", "item": "https://www.paradoxedge.com/testimonials" }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Results | Client Outcomes & Testimonials"
        description="Outcomes from senior leaders who have engaged ParadoxEdge coaching. NPS 91, zero detractors. More views. New ways. Inspired outcomes."
        canonicalUrl="/testimonials"
        keywords="executive coaching testimonials, leadership coaching reviews, coaching success stories, client results, NPS, leadership outcomes"
        jsonLd={testimonialsJsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <ElfsightWidget />
        <main>
          <ResultsHero />
          <FeaturedTestimonial />
          <MeasurableImpact />
          <NPS />
          <Voices />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Testimonials;
